import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  Chip,
  LinearProgress,
  TextField,
  useTheme,
  CircularProgress,
  Button,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import {
  ArrowBack,
  Menu as MenuIcon,
  MenuBook as MenuBookIcon,
  OpenInNew as OpenInNewIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  DeleteOutline as DeleteOutlineIcon,
  PrintRounded as PrintRoundedIcon,
} from "@mui/icons-material";

import MessageHistory from "../../../components/MessageHistory";
import ConversationContextBar from "../../../components/MessageHistory/ConversationContextBar";
import Placeholder from "../../../components/Placeholder";
import ConversationAccessDenied from "./ConversationAccessDenied";

import { getChannelIcon, getClientProfilePictureUrl, getInitials } from "../../../utils/utils";
import { useRequests } from "../../../hooks/useRequests";
import { useUsers } from "../../../hooks/useUsers";
import { useAgents } from "../../../hooks/useAgents";
import { useClients } from "../../../hooks/useClients";
import StatusChipSelector, {
  normalizeStatusInput,
} from "../../../components/StatusChipSelector";
import AssigneeSelector from "../../../components/AssigneeSelector";
import { getMessageHistoryWidth } from "../utils/layoutUtils";
import { getWhatsAppWindowState, resolveMessagingWindowLastInboundDate, channelTypeToMessagingWindowFamily } from "../../../utils/whatsappWindowUtils";
import {
  isWebchatBackground,
  isWebchatForeground,
  mergePresenceWithRecentWebchatMessages,
  resolveWebchatPresence,
} from "../../../utils/webchatTransport";
import {
  getMetaMessagingWindowState,
  getMetaChannelLabel,
  isMetaMessagingWindowChannel,
} from "../../../utils/metaMessagingWindowUtils";
import { formatMetaReferralLabel } from "../../../utils/metaMessageSocial";
import { formatWhatsappAttributionChip } from "../../../utils/whatsappMessageContext";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { fetchConversationKnowledgeMaterials } from "../../../api/client";
import TypeKnowledgeMaterialsDialog from "../../../components/TypeKnowledgeMaterialsDialog";
import BPeQuickSellButton from "../../../components/ConversationDetails/BPeQuickSellButton";
import { useChannels } from "../../../hooks/useChannels";
import { CHANNEL_TYPES, isWhatsAppQrChannelTipo } from "../../../constants/channelCatalog";
import {
  promotePersonalWhatsappToRequest,
  getConversationById,
  getConversationAccessLogs,
  getPersonalChatKeyFromSyntheticConversationId,
  postGupshupVoiceCallPermissionRequest,
  postGupshupTwilioWhatsappOutboundCall,
  markConversationWhatsappRead,
} from "../../../api/client";
import WhatsappQrCallBanner from "../../../components/MessageHistory/WhatsappQrCallBanner";
import WhatsappGroupManageDialog from "../../../components/MessageHistory/WhatsappGroupManageDialog";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  isWhatsappGroupConversation,
  resolveWhatsappGroupId,
} from "../../../utils/whatsappQrGroupMentions";
import { whatsappQrCapabilities } from "../../../utils/whatsappQrCapabilities";
import { useCallContext } from "../../../context/CallContext";
import { useBrand } from "../../../context/BrandContext";
import PromoteToRequestDialog from "../PromoteToRequestDialog";
import DuplicateClientDialog from "../../../components/clients/DuplicateClientDialog";
import { toE164Digits } from "../../../utils/phone";
import useChatConversationQueue from "../../../hooks/useChatConversationQueue";
import ConversationQueueNavigator from "../../../components/Chat/ConversationQueueNavigator";
import ConversationQueueKeyboardHint from "../../../components/Chat/ConversationQueueKeyboardHint";
import { printConversationRequest } from "../utils/printConversationRequest";
import {
  describeTimelineFiltersForPrint,
  loadConversationMessagesForPrint,
  readTimelineFiltersForPrint,
  selectMessagesForPrint,
  shouldIncludeTechnicalPayloads,
} from "../utils/printConversationMessages";
import { TIMELINE_FILTERS } from "../../../components/MessageHistory/messageTypeRegistry";
import { useCanViewApiAudit } from "../../../hooks/useCanViewApiAudit";
import { getRequestStatusChangeErrorMessage } from "../../../utils/status";
import {
  getAutomationBlockMessage,
  isAutomationRuleBlockError,
} from "../../../utils/automationBlockNotice";
import AutomationActionBlockedDialog from "../../../components/AutomationActionBlockedDialog";
import ClientRelationshipBadge from "../../../components/Chat/ClientRelationshipBadge";
import { useClientRelationships } from "../../../hooks/useClientRelationships";

/** Em falha, fetchData pode lançar o corpo da API `{ message, data }`. */
function formatVoiceChannelApiError(err, fallback) {
  const msg = typeof err?.message === "string" ? err.message : "";
  const d = err?.data && typeof err.data === "object" ? err.data : null;
  const parts = [];
  if (msg) parts.push(msg);
  if (d?.code) parts.push(String(d.code));
  if (d?.error != null) {
    parts.push(
      typeof d.error === "string" ? d.error : JSON.stringify(d.error).slice(0, 900)
    );
  } else if (d?.detail != null) {
    parts.push(
      typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail).slice(0, 900)
    );
  }
  if (Array.isArray(d?.voiceTokenConfigMissing) && d.voiceTokenConfigMissing.length) {
    parts.push(`[${d.voiceTokenConfigMissing.join(", ")}]`);
  }
  const joined = parts.filter(Boolean).join(" — ");
  return joined || fallback;
}

/* ================= MessageHistoryPane ================= */

const MessageHistoryPane = ({
  isMobile,
  containerStyle,
  selectedConversationId,
  selectedConversation,
  showConversationList,
  showConversationDetails,
  userData,
  isLoadingDetails,
  detailsAccessDenied = false,
  detailsDenied = null,
  initialLoading,
  isLoadingConversation,
  messagesSyncError,
  reloadConversationMessages,
  messages,
  onSendMessage,
  onEndConversation,
  onOpenPopup,
  onBack,
  onShowDetails,
  onUpdateConversation,
  onNewConversation,
  hasMoreMessages,
  isLoadingMoreMessages,
  onLoadMoreMessages,
  onRefetchConversationDetails,
  onDeleteMessage,
  onReactMessage,
  onEditWhatsappMessage,
  onForwardWhatsappMessage,
  onEditNote,
  onDeleteNote,
  onDeletePersonalConversation,
  onActivateConversation,
  queueConversations,
  queueFetchNextPage,
  queueHasNextPage,
  queueIsFetchingNextPage,
  queueTotalCount,
  hideListToggle = false,
  hideOpenPopupButton = false,
  hideDetailsToggle = false,
  popupCallMode = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const brand = useBrand();
  const canViewApiAudit = useCanViewApiAudit();
  const { prepareTwilioVoiceDevice } = useCallContext();
  const { updateMutation } = useRequests();
  // Precisa incluir ChatBots (ura=1) no seletor de responsável (transferir/atribuir).
  const { data: usersResp, isLoading: usersLoading } = useUsers(1, 1000, "", "todos", {
    order: "nome",
  });
  const { data: agentsResp } = useAgents(1, 200, "");
  const { updateMutation: updateClientMutation } = useClients(
    1,
    10,
    "",
    null,
    { enabled: false }
  );

  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const { data: channelsData } = useChannels(1, 200, "");

  const isPersonalThread =
    selectedConversation?.kind === "personal" ||
    String(selectedConversation?.id || "").startsWith("wa-personal:");

  const queueNavigatorEligible = Boolean(
    selectedConversation && !isPersonalThread && !isMobile && !showConversationList
  );
  const queueNavigationEnabled = Boolean(selectedConversation && !isPersonalThread);
  const queue = useChatConversationQueue(selectedConversationId, {
    onNavigate: onActivateConversation,
    enabled: queueNavigationEnabled,
    conversations: queueConversations,
    fetchNextPage: queueFetchNextPage,
    hasNextPage: queueHasNextPage,
    isFetchingNextPage: queueIsFetchingNextPage,
    totalCount: queueTotalCount,
  });

  const queueHasMultipleRequests = (queue.total ?? 0) > 1;
  const queueHasNavigationTargets =
    queue.canGoPrevious ||
    queue.canGoNext ||
    (queue.inQueue && queueHasMultipleRequests);
  const showQueueNavigator = Boolean(
    queueNavigatorEligible &&
      (!popupCallMode || (queueHasMultipleRequests && queueHasNavigationTargets))
  );
  const showLeadingHeaderControls = !hideListToggle || showQueueNavigator;
  const showQueueKeyboardHint = showQueueNavigator;

  const [promoteOpen, setPromoteOpen] = React.useState(false);
  const [groupManageOpen, setGroupManageOpen] = React.useState(false);
  const [promoteLoading, setPromoteLoading] = React.useState(false);
  const [promoteError, setPromoteError] = React.useState("");
  const [messagesDupOpen, setMessagesDupOpen] = React.useState(false);
  const [clientDupDialog, setClientDupDialog] = React.useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
    secondaryClientId: null,
  });

  const [gupshupVoiceBusy, setGupshupVoiceBusy] = React.useState(false);
  const [gupshupVoiceNotice, setGupshupVoiceNotice] = React.useState(null);
  const [composeChannelId, setComposeChannelId] = React.useState(null);
  const [deletePersonalOpen, setDeletePersonalOpen] = React.useState(false);
  const [deletePersonalBusy, setDeletePersonalBusy] = React.useState(false);
  const [statusUpdating, setStatusUpdating] = React.useState(false);
  const [printBusy, setPrintBusy] = React.useState(false);
  const printBusyRef = React.useRef(false);
  const [statusSnackbar, setStatusSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [automationBlockDialog, setAutomationBlockDialog] = React.useState({
    open: false,
    message: "",
  });

  const handleComposeChannelChange = React.useCallback((rawId) => {
    setComposeChannelId(rawId == null || rawId === "" ? null : String(rawId));
  }, []);

  const webchatPresenceLive = React.useMemo(
    () =>
      mergePresenceWithRecentWebchatMessages(
        resolveWebchatPresence(selectedConversation),
        messages
      ),
    [selectedConversation, messages]
  );

  React.useEffect(() => {
    setGupshupVoiceNotice(null);
    setGupshupVoiceBusy(false);
    setComposeChannelId(null);
    setDeletePersonalOpen(false);
    setDeletePersonalBusy(false);
    setStatusUpdating(false);
    setMessagesDupOpen(false);
  }, [selectedConversation?.id]);

  React.useEffect(() => {
    if (
      messagesSyncError?.code === "errors.clients.duplicate" &&
      Array.isArray(messagesSyncError.duplicates) &&
      messagesSyncError.duplicates.length
    ) {
      setMessagesDupOpen(true);
    }
  }, [messagesSyncError]);

  React.useEffect(() => {
    setGupshupVoiceNotice(null);
  }, [composeChannelId]);

  const openPromote = () => {
    setPromoteError("");
    setPromoteOpen(true);
  };

  const peerPhone = React.useMemo(() => {
    const chatKey =
      selectedConversation?.personalChatKey ||
      getPersonalChatKeyFromSyntheticConversationId(selectedConversation?.id);
    if (chatKey) {
      const raw = chatKey.startsWith("p:") ? chatKey.slice(2) : chatKey;
      const d = String(raw || "").replace(/\D/g, "");
      return d ? toE164Digits(d, "BR") || d : "";
    }
    const fromConv =
      selectedConversation?.celular ||
      selectedConversation?.phone ||
      selectedConversation?.telefone ||
      "";
    const digits = String(fromConv || "").replace(/\D/g, "");
    return digits ? toE164Digits(digits, "BR") || digits : "";
  }, [
    selectedConversation?.personalChatKey,
    selectedConversation?.id,
    selectedConversation?.celular,
    selectedConversation?.phone,
    selectedConversation?.telefone,
  ]);

  const handlePromoteSubmit = React.useCallback(async (payload) => {
    if (!isPersonalThread || typeof onActivateConversation !== "function") return;
    const chatKey =
      selectedConversation?.personalChatKey ||
      getPersonalChatKeyFromSyntheticConversationId(selectedConversation?.id);
    if (!chatKey) throw new Error(t("pane.promoteError"));

    setPromoteLoading(true);
    setPromoteError("");
    try {
      const res = await promotePersonalWhatsappToRequest({
        chatKey,
        ...payload,
      });
      const convId = res?.data?.conversationId ?? res?.conversationId;
      if (!convId) throw new Error(t("pane.promoteError"));
      const dto = await getConversationById(convId);
      const client = dto?.client || {};
      const conversation = {
        id: dto.id,
        name: client.name ?? dto.name ?? "",
        nome: client.name ?? dto.name ?? "",
        lastMessage: dto.lastMessage || "",
        timestamp: dto.timestamp,
        channel: dto.channel,
        status: dto.status,
        assignedTo: dto.userName ?? dto.assignedTo ?? null,
        unread: dto.unread || 0,
        statusLabel: t("page.statusInProgress"),
      };
      setPromoteOpen(false);
      onActivateConversation(conversation);
    } catch (e) {
      const msg =
        (typeof e?.message === "string" && e.message) ||
        (Array.isArray(e?.message) && e.message[0]) ||
        t("pane.promoteError");
      setPromoteError(msg);
      throw e;
    } finally {
      setPromoteLoading(false);
    }
  }, [isPersonalThread, selectedConversation, onActivateConversation, t]);

  const getWidth = () => {
    if (isMobile) return "100%";
    return getMessageHistoryWidth({
      selectedConversation,
      showConversationList,
      showConversationDetails,
    });
  };

  const displayName =
    selectedConversation?.name || selectedConversation?.nome || "";

  const displayProtocol =
    selectedConversation?.protocol || selectedConversation?.id || "";

  const handleCopyProtocol = React.useCallback(async () => {
    const value = displayProtocol != null ? String(displayProtocol).trim() : "";
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setStatusSnackbar({
        open: true,
        message: t("pane.protocolCopied", { id: value }),
        severity: "success",
      });
    } catch {
      setStatusSnackbar({
        open: true,
        message: t("pane.protocolCopyFailed"),
        severity: "error",
      });
    }
  }, [displayProtocol, t]);

  const mergeAttemptedPayload = React.useMemo(() => {
    if (!selectedConversation || isPersonalThread) return {};
    const idProjeto = selectedConversation.projectId ?? selectedConversation.id_projeto ?? null;
    const pid = idProjeto != null && String(idProjeto).trim() !== "" ? Number(idProjeto) : null;
    const onlyDigits = (v) => String(v ?? "").replace(/\D+/g, "");
    const payload = {
      ...(Number.isFinite(pid) ? { id_projeto: pid } : {}),
      nome: selectedConversation.nome || selectedConversation.name || null,
      email: selectedConversation.email || null,
      celular: onlyDigits(selectedConversation.celular) || null,
      telefone: onlyDigits(selectedConversation.telefone || selectedConversation.phone) || null,
      whatsapp: onlyDigits(selectedConversation.whatsapp) || null,
      cpf: onlyDigits(selectedConversation.cpf) || null,
      cnpj: onlyDigits(selectedConversation.cnpj) || null,
      id_interno:
        selectedConversation.id_interno != null && String(selectedConversation.id_interno).trim() !== ""
          ? String(selectedConversation.id_interno).trim()
          : null,
    };
    return payload;
  }, [selectedConversation, isPersonalThread]);

  const mergeSecondaryClientId = React.useMemo(() => {
    const raw = selectedConversation?.clientId;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [selectedConversation?.clientId]);

  const awaitingConversationDetails =
    selectedConversationId != null &&
    !selectedConversation &&
    isLoadingDetails;

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState("");
  const [knowledgeMaterialsOpen, setKnowledgeMaterialsOpen] = React.useState(false);

  const conversationIdForKnowledge =
    selectedConversation?.id != null && !isPersonalThread ? Number(selectedConversation.id) : null;

  const { data: knowledgeMaterialsMeta } = useQuery(
    ["conversationKnowledgeMaterials", conversationIdForKnowledge],
    () => fetchConversationKnowledgeMaterials(conversationIdForKnowledge),
    {
      enabled: Number.isFinite(conversationIdForKnowledge) && conversationIdForKnowledge > 0,
      staleTime: 120_000,
      refetchOnWindowFocus: false,
    }
  );

  const showKnowledgeMaterialsIcon = Boolean(knowledgeMaterialsMeta?.hasKnowledge);

  React.useEffect(() => {
    if (!selectedConversation) return;
    setIsEditingName(false);
    setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
  }, [selectedConversation?.id]);

  const canEditName =
    Boolean(selectedConversation?.clientId) && !isPersonalThread && !popupCallMode;

  const headerClientId =
    !isPersonalThread && selectedConversation?.clientId
      ? selectedConversation.clientId
      : null;
  const { data: headerRelationships = [] } = useClientRelationships(headerClientId);

  const commitName = (rawValue) => {
    if (!selectedConversation?.clientId) return;
    const next = String(rawValue ?? "").trim();
    if (!next) return;

    const current = String(selectedConversation?.nome || selectedConversation?.name || "").trim();
    if (next === current) {
      setIsEditingName(false);
      return;
    }

    updateClientMutation.mutate(
      { id: selectedConversation.clientId, nome: next },
      {
        onSuccess: () => {
          onUpdateConversation?.({ nome: next, name: next });
          setIsEditingName(false);
        },
        onError: (err) => {
          console.error(t("pane.errClientName"), err);
          if (err?.code === "errors.clients.duplicate") {
            setClientDupDialog({
              open: true,
              duplicates: err?.data?.duplicates || [],
              attemptedPayload: {
                id_projeto:
                  selectedConversation?.projectId ??
                  selectedConversation?.id_projeto ??
                  null,
                nome: next,
              },
              secondaryClientId: Number(selectedConversation.clientId),
            });
          }
        },
      }
    );
  };

  const cancelEditName = () => {
    setIsEditingName(false);
    setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
  };

  const parseTags = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split(",").map((t) => t.trim()).filter(Boolean);
  };
  const clientTags = parseTags(
    selectedConversation?.clientTags ?? selectedConversation?.client?.tags ?? ""
  );
  const requestTags = parseTags(selectedConversation?.tags ?? "");
  const hasTags = clientTags.length > 0 || requestTags.length > 0;
  const metaReferralLabel =
    formatWhatsappAttributionChip(selectedConversation?.whatsappAttribution) ||
    formatMetaReferralLabel(selectedConversation?.metaReferral);

  const displayChannel =
    selectedConversation?.channelLabel || selectedConversation?.channel || "";

  const channelType =
    selectedConversation?.channelType ??
    selectedConversation?.channel_type ??
    (() => {
      const ch = (selectedConversation?.channel ?? selectedConversation?.channelLabel ?? "").toString().toLowerCase();
      if (ch.includes("whatsapp")) return "WhatsApp";
      return null;
    })();
  const channelIdForWindow =
    composeChannelId ??
    selectedConversation?.channelId ??
    selectedConversation?.id_origem ??
    selectedConversation?.channel_id ??
    null;
  const channelRowForWindow = React.useMemo(() => {
    const channelList = Array.isArray(channelsData?.channels) ? channelsData.channels : [];
    if (channelIdForWindow == null) return null;
    return channelList.find((c) => String(c.id) === String(channelIdForWindow)) || null;
  }, [channelsData, channelIdForWindow]);

  const channelTipoForWindow = React.useMemo(() => {
    const t = channelRowForWindow?.tipo;
    return t != null && t !== "" ? Number(t) : null;
  }, [channelRowForWindow]);
  const groupJidForManage = React.useMemo(
    () => resolveWhatsappGroupId(selectedConversation, messages) || "",
    [selectedConversation, messages]
  );
  const canManageWhatsappGroup = Boolean(
    !isPersonalThread &&
      isWhatsAppQrChannelTipo(channelTipoForWindow) &&
      whatsappQrCapabilities(channelRowForWindow?.whatsapp_qr_api_version).groupAdmin &&
      (isWhatsappGroupConversation(selectedConversation, messages) || groupJidForManage)
  );

  const windowChannelFamily = React.useMemo(
    () => channelTypeToMessagingWindowFamily(channelType, channelTipoForWindow),
    [channelType, channelTipoForWindow]
  );

  const lastClientDate = React.useMemo(() => {
    if (!windowChannelFamily) return null;
    return resolveMessagingWindowLastInboundDate({
      messages,
      messagingWindows: selectedConversation?.messagingWindows,
      channelFamily: windowChannelFamily,
      conversationChannelFamily: windowChannelFamily,
    });
  }, [
    messages,
    selectedConversation?.messagingWindows,
    windowChannelFamily,
  ]);

  const contactPhoneForVoice =
    selectedConversation?.celular ||
    selectedConversation?.client?.phone ||
    selectedConversation?.phoneNumber ||
    "";

  const showGupshupSipVoiceBar =
    !isPersonalThread &&
    Boolean(selectedConversation) &&
    channelRowForWindow &&
    channelTipoForWindow === CHANNEL_TYPES.WHATSAPP_GUPSHUP &&
    String(channelRowForWindow.whatsapp_calling_mode || "").trim().toLowerCase() === "gupshup_sip";

  const handleGupshupRequestPermission = async () => {
    if (!channelIdForWindow || !contactPhoneForVoice.trim()) {
      setGupshupVoiceNotice({
        severity: "warning",
        text: t("pane.whatsappVoice.missingPhone"),
      });
      return;
    }
    setGupshupVoiceBusy(true);
    setGupshupVoiceNotice(null);
    try {
      await postGupshupVoiceCallPermissionRequest(Number(channelIdForWindow), {
        to: contactPhoneForVoice.trim(),
      });
      setGupshupVoiceNotice({ severity: "success", text: t("pane.whatsappVoice.permissionSent") });
    } catch (e) {
      const msg = formatVoiceChannelApiError(e, t("pane.whatsappVoice.genericError"));
      setGupshupVoiceNotice({ severity: "error", text: String(msg) });
    } finally {
      setGupshupVoiceBusy(false);
    }
  };

  const handleGupshupTwilioCall = async () => {
    const uid = userData?.id;
    if (!channelIdForWindow || !uid) {
      setGupshupVoiceNotice({
        severity: "warning",
        text: t("pane.whatsappVoice.missingUser"),
      });
      return;
    }
    if (!contactPhoneForVoice.trim()) {
      setGupshupVoiceNotice({
        severity: "warning",
        text: t("pane.whatsappVoice.missingPhone"),
      });
      return;
    }
    const agentTo = `client:user_${uid}_ch_${channelIdForWindow}`;
    const leadId = selectedConversation?.id != null ? Number(selectedConversation.id) : null;
    setGupshupVoiceBusy(true);
    setGupshupVoiceNotice(null);
    try {
      if (typeof prepareTwilioVoiceDevice === "function") {
        await prepareTwilioVoiceDevice(Number(channelIdForWindow), leadId);
      }
      await postGupshupTwilioWhatsappOutboundCall(Number(channelIdForWindow), {
        to: contactPhoneForVoice.trim(),
        agentTo,
        ...(leadId && !Number.isNaN(leadId) ? { leadId } : {}),
      });
      setGupshupVoiceNotice({ severity: "success", text: t("pane.whatsappVoice.callStarted") });
    } catch (e) {
      const msg = formatVoiceChannelApiError(e, t("pane.whatsappVoice.genericError"));
      setGupshupVoiceNotice({ severity: "error", text: String(msg) });
    } finally {
      setGupshupVoiceBusy(false);
    }
  };
  const whatsAppWindowState =
    channelType &&
    String(channelType).toLowerCase() === "whatsapp" &&
    !isWhatsAppQrChannelTipo(channelTipoForWindow)
      ? getWhatsAppWindowState(lastClientDate)
      : null;
  const showWhatsAppWindowIcon =
    whatsAppWindowState &&
    (whatsAppWindowState.status === "aboutToExpire" || whatsAppWindowState.status === "expired");

  const metaWindowState = isMetaMessagingWindowChannel(channelType, channelTipoForWindow)
    ? getMetaMessagingWindowState(lastClientDate, {
        channelLabel: getMetaChannelLabel(channelType),
      })
    : null;
  const showMetaWindowIcon =
    metaWindowState &&
    (metaWindowState.status === "aboutToExpire" || metaWindowState.status === "expired");

  React.useEffect(() => {
    if (!selectedConversation?.id || !isWhatsAppQrChannelTipo(channelTipoForWindow)) return undefined;
    const caps = whatsappQrCapabilities(channelRowForWindow?.whatsapp_qr_api_version);
    if (!caps.markRead) return undefined;
    markConversationWhatsappRead(selectedConversation.id).catch(() => {});
    return undefined;
  }, [selectedConversation?.id, channelTipoForWindow, channelRowForWindow?.whatsapp_qr_api_version]);

  const assignedToValue =
    selectedConversation?.assignedTo ?? selectedConversation?.userName ?? null;

  const assignedIsQueueLabel =
    typeof assignedToValue === "string" &&
    ["fila", "queue"].includes(assignedToValue.toLowerCase());

  const hasUserAssigned =
    assignedToValue !== null &&
    assignedToValue !== undefined &&
    assignedToValue !== 0 &&
    assignedToValue !== "0" &&
    assignedToValue !== "" &&
    !assignedIsQueueLabel;

  const agentName =
    selectedConversation?.agentName ?? selectedConversation?.agent_name ?? null;
  const rawAgentId =
    selectedConversation?.agentId ?? selectedConversation?.id_agente ?? null;
  const hasAgentAssigned =
    rawAgentId !== null &&
    rawAgentId !== "" &&
    rawAgentId !== 0 &&
    rawAgentId !== "0";

  const currentAssignee = React.useMemo(() => {
    if (!selectedConversation) {
      return { type: "queue", id: null, label: t("pane.queue") };
    }

    if (isPersonalThread) {
      return {
        type: "user",
        id: userData?.id ?? null,
        label: t("pane.myRequest"),
      };
    }

    const status = normalizeStatusInput(selectedConversation?.status);
    const isClosed = status === 2 || status === 4 || status === 5;

    if (hasAgentAssigned) {
      const id = rawAgentId;
      const label = agentName || t("pane.aiAgent");
      return { type: "agent", id, label };
    }

    if (hasUserAssigned || selectedConversation?.userId) {
      const id = selectedConversation?.userId ?? null;
      const fromList =
        id != null ? users.find((u) => String(u.id) === String(id)) || null : null;
      const isChatBot =
        fromList
          ? Number(fromList.ura) === 1
          : Number(selectedConversation?.userUra) === 1;
      const baseLabel =
        assignedToValue || selectedConversation?.userName || "";

      const isCurrentUser =
        (id != null && String(id) === String(userData?.id)) ||
        (baseLabel && baseLabel === userData?.nome);

      const label = isCurrentUser
        ? t("pane.myRequest")
        : baseLabel || (isChatBot ? t("pane.chatBot") : t("pane.user"));

      return { type: "user", id, label, isChatBot };
    }

    return {
      type: "queue",
      id: null,
      label: isClosed ? t("pane.unassigned") : t("pane.queue"),
    };
  }, [
    hasAgentAssigned,
    hasUserAssigned,
    agentName,
    assignedToValue,
    t,
    selectedConversation,
    userData,
    users,
    isPersonalThread,
  ]);

  const handleStatusChange = (newStatus) => {
    if (!selectedConversation?.id) return;
    if (isPersonalThread) return;
    if (statusUpdating) return;

    setStatusUpdating(true);
    updateMutation.mutate(
      { id: selectedConversation.id, status: newStatus },
      {
        onSuccess: () => {
          setStatusUpdating(false);
          onUpdateConversation?.({ status: newStatus });
          if (typeof onRefetchConversationDetails === "function") {
            onRefetchConversationDetails();
          }
        },
        onError: (err) => {
          setStatusUpdating(false);
          console.error(t("pane.errStatus"), err);
          const fallback = t("pane.statusChangeError");
          if (isAutomationRuleBlockError(err)) {
            setAutomationBlockDialog({
              open: true,
              message: getAutomationBlockMessage(err, fallback),
            });
            return;
          }
          setStatusSnackbar({
            open: true,
            message: getRequestStatusChangeErrorMessage(err, fallback),
            severity: "error",
          });
        },
      }
    );
  };

  const handlePrintRequest = React.useCallback(async () => {
    if (!selectedConversation || isPersonalThread || printBusyRef.current) return;
    printBusyRef.current = true;
    setPrintBusy(true);

    try {
    const filters = readTimelineFiltersForPrint(canViewApiAudit);
    let source = Array.isArray(messages) ? messages : [];
    let partialHistory = false;
    let partialLabel = t("pane.print.partialHistory");

    try {
      const loaded = await loadConversationMessagesForPrint(selectedConversation.id);
      source = loaded.messages;
      partialHistory = Boolean(loaded.truncated);
      if (filters.includes(TIMELINE_FILTERS.ACCESS)) {
        try {
          const access = await getConversationAccessLogs(selectedConversation.id);
          if (Array.isArray(access) && access.length) {
            source = [...source, ...access];
          }
        } catch (accessErr) {
          console.error("Erro ao carregar acessos para impressão:", accessErr);
          partialHistory = true;
          if (!loaded.truncated) {
            partialLabel = t("pane.print.accessFallback");
          }
        }
      }
    } catch (err) {
      console.error("Erro ao carregar histórico completo para impressão:", err);
      source = Array.isArray(messages) ? messages : [];
      partialHistory = true;
      partialLabel = t("pane.print.loadFallback");
    }

    try {
      const printable = selectMessagesForPrint(source, {
        activeFilters: filters,
        canViewApiAudit,
      });
      const payloads = shouldIncludeTechnicalPayloads(filters, canViewApiAudit);

      printConversationRequest({
      conversation: selectedConversation,
      messages: printable,
      userData,
      partialHistory,
      includeApiPayloads: payloads.api,
      includeAiPayloads: payloads.ai,
      logoUrl: `${window.location.origin}${brand.assets.logo.light}`,
      printedBy: userData?.nome || userData?.name || "",
      copyright: t("pane.print.copyright", {
        year: new Date().getFullYear(),
        brand: brand.name,
      }),
      labels: {
        title: t("pane.print.title"),
        printedAt: t("pane.print.printedAt"),
        printedBy: t("pane.print.printedBy"),
        brandName: brand.name,
        clientSection: t("pane.print.clientSection"),
        requestSection: t("pane.print.requestSection"),
        messagesSection: t("pane.print.messagesSection"),
        filtersNote: describeTimelineFiltersForPrint(filters, t),
        partialHistory: partialLabel,
        auditRequest: t("pane.print.auditRequest"),
        auditResponse: t("pane.print.auditResponse"),
        auditWebhook: t("pane.print.auditWebhook"),
        auditHeaders: t("pane.print.auditHeaders"),
        auditPlatformResponse: t("pane.print.auditPlatformResponse"),
        auditRules: t("pane.print.auditRules"),
        auditContext: t("pane.print.auditContext"),
        auditUsage: t("pane.print.auditUsage"),
        popupBlocked: t("pane.print.popupBlocked"),
        noClientData: t("pane.print.noClientData"),
        noRequestData: t("pane.print.noRequestData"),
        noMessages: t("pane.print.noMessages"),
        name: t("pane.print.fields.name"),
        email: t("pane.print.fields.email"),
        mobile: t("pane.print.fields.mobile"),
        phone: t("pane.print.fields.phone"),
        whatsapp: t("pane.print.fields.whatsapp"),
        cpf: t("pane.print.fields.cpf"),
        identity: t("pane.print.fields.identity"),
        birthDate: t("pane.print.fields.birthDate"),
        gender: t("pane.print.fields.gender"),
        internalId: t("pane.print.fields.internalId"),
        clientType: t("pane.print.fields.clientType"),
        facebook: t("pane.print.fields.facebook"),
        instagram: t("pane.print.fields.instagram"),
        linkedin: t("pane.print.fields.linkedin"),
        address: t("pane.print.fields.address"),
        clientTags: t("pane.print.fields.clientTags"),
        protocol: t("pane.protocol"),
        status: t("pane.print.fields.status"),
        project: t("pane.print.fields.project"),
        channel: t("pane.print.fields.channel"),
        type: t("pane.print.fields.type"),
        assignee: t("pane.print.fields.assignee"),
        potential: t("pane.print.fields.potential"),
        nextContact: t("pane.print.fields.nextContact"),
        invoice: t("pane.print.fields.invoice"),
        product: t("pane.print.fields.product"),
        campaign: t("pane.print.fields.campaign"),
        additionalText: t("pane.print.fields.additionalText"),
        additionalDate: t("pane.print.fields.additionalDate"),
        value: t("pane.print.fields.value"),
        requestTags: t("pane.print.fields.requestTags"),
        via: t("pane.print.via"),
        client: t("pane.print.sender.client"),
        agent: t("pane.print.sender.agent"),
        aiAgent: t("pane.aiAgent"),
        me: t("pane.myRequest"),
        internalNote: t("pane.print.sender.internalNote"),
        systemLog: t("pane.print.sender.systemLog"),
        other: t("pane.print.sender.other"),
        queue: t("pane.queue"),
      },
      });
    } catch (printErr) {
      console.error("Erro ao montar a impressão da solicitação:", printErr);
      window.alert(t("pane.print.printFailed"));
    }
    } catch (printErr) {
      console.error("Erro ao montar a impressão da solicitação:", printErr);
      window.alert(t("pane.print.printFailed"));
    } finally {
      printBusyRef.current = false;
      setPrintBusy(false);
    }
  }, [selectedConversation, isPersonalThread, messages, userData, t, brand, canViewApiAudit]);

  const handleAssigneeChange = (next) => {
    if (!selectedConversation?.id || !next) return;
    if (isPersonalThread) return;

    const payload = { id: selectedConversation.id };
    const updates = {};

    if (next.type === "queue") {
      payload.id_usuario = null;
      payload.id_agente = null;
      updates.userId = null;
      updates.assignedTo = null;
      updates.userName = null;
      updates.agentId = null;
      updates.agentName = null;
    } else if (next.type === "agent") {
      // Resolve o ID do agente primeiro a partir do objeto bruto,
      // depois cai para o next.id e, por último, para o nome.
      let agentId = null;

      if (next.raw) {
        const a = next.raw;
        agentId =
          a.id ??
          a.id_agente ??
          a.agentId ??
          null;
      }

      if (!agentId && next.id) {
        agentId = next.id;
      }

      if (!agentId && next.label) {
        const found = agents.find((a) => {
          const name = a.name ?? a.nome ?? "";
          return name === next.label;
        });
        if (found) {
          agentId =
            found.id ??
            found.id_agente ??
            found.agentId ??
            null;
        }
      }

      payload.id_agente = agentId || null;
      payload.id_usuario = null;
      updates.agentId = payload.id_agente;
      updates.agentName = next.label;
      updates.userId = null;
      updates.assignedTo = null;
      updates.userName = null;
    } else if (next.type === "user") {
      payload.id_usuario = next.id ? Number(next.id) : null;
      payload.id_agente = null;
      updates.userId = payload.id_usuario;
      updates.assignedTo = next.label;
      updates.userName = next.label;
      updates.agentId = null;
      updates.agentName = null;
    }

    updateMutation.mutate(payload, {
      onSuccess: () => {
        onUpdateConversation?.(updates);
        if (typeof onRefetchConversationDetails === "function") {
          onRefetchConversationDetails();
        }
      },
      onError: (err) => {
        console.error(t("pane.errAssignee"), err);
      },
    });
  };

  // console.log("messageHistory selectedConversation", selectedConversation);

  return (
    <Box
      className="tour-chat-message-pane"
      width={getWidth()}
      bgcolor={theme.palette.background.paper}
      display="flex"
      flexDirection="column"
      height="100%"
      sx={containerStyle}
    >
      <Box
        className="tour-chat-message-header"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={popupCallMode ? 1 : isMobile ? 0.75 : 1}
        gap={popupCallMode ? 1 : isMobile ? 0.75 : 0.5}
        sx={{
          minWidth: 0,
          ...(popupCallMode
            ? {
                minHeight: 48,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }
            : {}),
        }}
      >
        {showLeadingHeaderControls && (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ...(popupCallMode
                ? {
                    order: 1,
                  }
                : {}),
            }}
          >
            {!hideListToggle && (
              isMobile ? (
                <IconButton onClick={onBack}>
                  <ArrowBack />
                </IconButton>
              ) : (
                !showConversationList && (
                  <IconButton onClick={onBack}>
                    <MenuIcon />
                  </IconButton>
                )
              )
            )}
            {showQueueNavigator && (
              <Tooltip title={t("queue.keyboardHint")} enterDelay={600}>
                <Box>
                  <ConversationQueueNavigator
                    protocolId={selectedConversation.id}
                    position={queue.position}
                    total={queue.total}
                    canGoPrevious={queue.canGoPrevious}
                    canGoNext={queue.canGoNext}
                    isFetchingNextPage={queue.isFetchingNextPage}
                    inQueue={queue.inQueue}
                    onPrevious={queue.goPrevious}
                    onNext={queue.goNext}
                    compact
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        {selectedConversation && !detailsAccessDenied && !popupCallMode && isMobile && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
          >
            <Avatar
              src={
                getClientProfilePictureUrl(
                  selectedConversation?.profile_picture ??
                    selectedConversation?.client?.profile_picture ??
                    selectedConversation?.client?.imagem
                ) || undefined
              }
              sx={{ width: 36, height: 36, flexShrink: 0 }}
            >
              {getInitials(displayName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <Box display="flex" alignItems="center" gap={0.25} sx={{ minWidth: 0 }}>
                {isEditingName ? (
                  <TextField
                    size="small"
                    value={nameDraft}
                    autoFocus
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={() => commitName(nameDraft)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitName(nameDraft);
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEditName();
                      }
                    }}
                    inputProps={{
                      "aria-label": t("pane.editClientName"),
                      style: { fontWeight: 700 },
                    }}
                    sx={{
                      "& .MuiInputBase-root": { fontSize: "1rem", fontWeight: "bold" },
                      minWidth: 0,
                      flex: 1,
                    }}
                  />
                ) : (
                  <Typography
                    variant="subtitle1"
                    noWrap
                    title={displayName}
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2,
                      cursor: canEditName ? "pointer" : "default",
                      minWidth: 0,
                      flex: 1,
                    }}
                    onClick={() => {
                      if (!canEditName) return;
                      setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                      setIsEditingName(true);
                    }}
                  >
                    {displayName}
                  </Typography>
                )}
                {canEditName && !isEditingName && (
                  <Tooltip title={t("pane.editClientName")}>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ flexShrink: 0 }}
                        onClick={() => {
                          setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                          setIsEditingName(true);
                        }}
                        disabled={updateClientMutation.isLoading}
                        aria-label={t("pane.editClientName")}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
              {!isPersonalThread && (
                <ConversationContextBar
                  compact
                  messages={messages ?? []}
                  protocol={displayProtocol}
                  onProtocolClick={handleCopyProtocol}
                  hideGroupChip
                />
              )}
            </Box>
          </Box>
        )}

        {selectedConversation && !detailsAccessDenied && !popupCallMode && !isMobile && (
          <Box
            display="flex"
            alignItems="center"
            gap={popupCallMode ? 1 : 1.5}
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              ...(popupCallMode ? { order: 1 } : {}),
            }}
          >
            <Avatar
              src={
                getClientProfilePictureUrl(
                  selectedConversation?.profile_picture ??
                    selectedConversation?.client?.profile_picture ??
                    selectedConversation?.client?.imagem
                ) || undefined
              }
              sx={{
                width: popupCallMode ? 40 : 45,
                height: popupCallMode ? 40 : 45,
                flexShrink: 0,
              }}
            >
              {getInitials(displayName)}
            </Avatar>

            <Box
              display="flex"
              flexDirection="column"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                ...(popupCallMode ? { gap: 0.2 } : {}),
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                sx={{
                  minWidth: 0,
                  ...(popupCallMode ? { maxWidth: "100%" } : {}),
                }}
              >
                {isEditingName ? (
                  <TextField
                    size="small"
                    value={nameDraft}
                    autoFocus
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={() => commitName(nameDraft)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitName(nameDraft);
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEditName();
                      }
                    }}
                    inputProps={{
                      "aria-label": t("pane.editClientName"),
                      style: { fontWeight: 700 },
                    }}
                    sx={{
                      "& .MuiInputBase-root": { fontSize: "1.2rem", fontWeight: "bold" },
                      minWidth: isMobile ? 0 : 220,
                      flex: 1,
                    }}
                  />
                ) : (
                  <Typography
                    variant="h6"
                    noWrap
                    title={displayName}
                    sx={{
                      fontWeight: "bold",
                      lineHeight: popupCallMode ? 1.1 : 1.2,
                      cursor: canEditName ? "pointer" : "default",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      ...(popupCallMode ? { fontSize: "1.05rem" } : {}),
                    }}
                    onClick={() => {
                      if (!canEditName) return;
                      setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                      setIsEditingName(true);
                    }}
                  >
                    {displayName}
                  </Typography>
                )}

                {canEditName && (
                  isEditingName ? (
                    <Box sx={{ flexShrink: 0, display: "flex" }}>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitName(nameDraft)}
                        disabled={updateClientMutation.isLoading}
                        aria-label={t("pane.saveClientName")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={cancelEditName}
                        disabled={updateClientMutation.isLoading}
                        aria-label={t("pane.cancelClientNameEdit")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Tooltip title={t("pane.editClientName")}>
                      <span>
                        <IconButton
                          size="small"
                          sx={{ flexShrink: 0 }}
                          onClick={() => {
                            setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                            setIsEditingName(true);
                          }}
                          disabled={updateClientMutation.isLoading}
                          aria-label={t("pane.editClientName")}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                )}
              </Box>

              <Box
                mt={popupCallMode ? 0 : "auto"}
                display="flex"
                gap={popupCallMode ? 0.5 : 1}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  minWidth: 0,
                  rowGap: popupCallMode ? 0.25 : 0.5,
                  ...(popupCallMode
                    ? {
                        "& .MuiChip-root": {
                          maxWidth: 180,
                        },
                      }
                    : {}),
                }}
              >
                {!isMobile &&
                  !isPersonalThread &&
                  displayChannel &&
                  (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35, minWidth: 0 }}>
                      {getChannelIcon(
                        displayChannel,
                        selectedConversation?.channelType || null,
                        channelRowForWindow?.tipo ?? null
                      )}
                      {popupCallMode && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 180 }}
                        >
                          {displayChannel}
                        </Typography>
                      )}
                    </Box>
                  )}

                {showWhatsAppWindowIcon && (
                  <Tooltip title={whatsAppWindowState.tooltip} placement="bottom" arrow>
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                      <ScheduleIcon
                        sx={{
                          fontSize: 22,
                          color:
                            whatsAppWindowState.status === "aboutToExpire"
                              ? theme.palette.warning.main
                              : theme.palette.error.main,
                        }}
                        aria-label={t("pane.whatsapp24h")}
                      />
                    </Box>
                  </Tooltip>
                )}

                {showMetaWindowIcon && (
                  <Tooltip title={metaWindowState.tooltip} placement="bottom" arrow>
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                      <ScheduleIcon
                        sx={{
                          fontSize: 22,
                          color:
                            metaWindowState.status === "aboutToExpire"
                              ? theme.palette.warning.main
                              : theme.palette.error.main,
                        }}
                        aria-label={t("pane.metaMessagingWindow")}
                      />
                    </Box>
                  </Tooltip>
                )}

                {!popupCallMode && !isPersonalThread && (
                  <Tooltip
                    title={t("pane.protocolCopyTooltip", { id: displayProtocol })}
                  >
                    <Chip
                      label={`${t("pane.protocol")}: ${displayProtocol}`}
                      size="small"
                      onClick={handleCopyProtocol}
                      clickable
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        height: "24px",
                        flexShrink: 0,
                        maxWidth: "none",
                        "& .MuiChip-label": {
                          overflow: "visible",
                          textOverflow: "clip",
                          whiteSpace: "nowrap",
                          px: 1,
                        },
                      }}
                    />
                  </Tooltip>
                )}

                {!popupCallMode && !isPersonalThread && headerClientId && (
                  <ClientRelationshipBadge
                    clientId={headerClientId}
                    relationships={headerRelationships}
                    count={selectedConversation?.relationshipsCount}
                    hint={selectedConversation?.relationshipHint}
                  />
                )}

                {!popupCallMode && !isPersonalThread && metaReferralLabel && (
                  <Tooltip title={metaReferralLabel}>
                    <Chip
                      label={metaReferralLabel}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        height: "24px",
                        maxWidth: "180px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    />
                  </Tooltip>
                )}

                {/* Seletor unificado: Fila / Usuário / Agente de IA */}
                {!popupCallMode && !isMobile && !isPersonalThread && (
                  <AssigneeSelector
                    value={currentAssignee}
                    onChange={handleAssigneeChange}
                    users={users}
                    agents={agents}
                    loading={usersLoading}
                    size="small"
                  />
                )}

                {/* Status como chip clicável com menu de opções */}
                {!isMobile &&
                  !popupCallMode &&
                  !isPersonalThread &&
                  selectedConversation?.status !== null &&
                  selectedConversation?.status !== undefined && (
                    <StatusChipSelector
                      currentStatus={selectedConversation.status}
                      onStatusChange={handleStatusChange}
                      loading={statusUpdating}
                    />
                  )}

                {canManageWhatsappGroup ? (
                  <Chip
                    icon={<GroupsIcon sx={{ fontSize: "16px !important" }} />}
                    size="small"
                    label={t("context.groupWhatsapp", "Grupo WhatsApp")}
                    onClick={() => setGroupManageOpen(true)}
                    clickable
                    sx={{ fontSize: "0.75rem", height: 24 }}
                  />
                ) : null}

                {isPersonalThread && (
                  <Chip
                    size="small"
                    label={t("pane.personalWhatsappBadge")}
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 24 }}
                  />
                )}
                {!isPersonalThread &&
                  (selectedConversation?.webchatRedirectEnabled ||
                    Number(selectedConversation?.webchatAtivo) === 1 ||
                    selectedConversation?.webchatAtivo === true) && (
                    <Chip
                      size="small"
                      label={
                        isWebchatForeground(webchatPresenceLive)
                          ? t("pane.webchatOpen", "Web Chat aberto")
                          : isWebchatBackground(webchatPresenceLive)
                            ? t("pane.webchatAway", "Web Chat em segundo plano")
                            : t("pane.webchatHidden", "Web Chat não está visível")
                      }
                      color={
                        isWebchatForeground(webchatPresenceLive)
                          ? "success"
                          : isWebchatBackground(webchatPresenceLive)
                            ? "info"
                            : "warning"
                      }
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", height: 24 }}
                    />
                  )}
              </Box>

              {/* Tags do cliente e da solicitação */}
              {!popupCallMode && hasTags && (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={0.5}
                  alignItems="center"
                  mt={popupCallMode ? 0 : 0.5}
                >
                  {clientTags.map((tag, i) => (
                    <Chip
                      key={`c-${i}`}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                  {requestTags.map((tag, i) => (
                    <Chip
                      key={`r-${i}`}
                      label={tag}
                      size="small"
                      color="primary"
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                </Box>
              )}
              {!isPersonalThread && (
                <ConversationContextBar
                  messages={messages ?? []}
                  hideGroupChip
                />
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            ...(popupCallMode ? { order: 2, minWidth: 36, ml: "auto" } : {}),
          }}
        >
          {isPersonalThread && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={openPromote}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {t("pane.promoteToRequest")}
            </Button>
          )}
          {isPersonalThread && typeof onDeletePersonalConversation === "function" && (
            <Tooltip title={t("pane.deletePersonalTooltip")}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={t("pane.deletePersonalTooltip")}
                  onClick={() => setDeletePersonalOpen(true)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showKnowledgeMaterialsIcon && !isMobile && (
            <Tooltip title={t("pane.knowledgeMaterials", { defaultValue: "Materiais do tipo/subtipo" })}>
              <IconButton
                size="small"
                onClick={() => setKnowledgeMaterialsOpen(true)}
                aria-label={t("pane.knowledgeMaterials", { defaultValue: "Materiais do tipo/subtipo" })}
                sx={{
                  color: "text.secondary",
                  opacity: 0.85,
                  "&:hover": { opacity: 1, color: "primary.main" },
                }}
              >
                <MenuBookIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {selectedConversation?.id && !isPersonalThread && !isMobile && (
            <BPeQuickSellButton
              selectedConversation={selectedConversation}
              onSuccess={onRefetchConversationDetails}
            />
          )}
          {selectedConversation?.id && !isPersonalThread && (
            <Tooltip title={printBusy ? t("pane.print.preparing") : t("pane.print.tooltip")}>
              <span>
                <IconButton
                  size="small"
                  onClick={handlePrintRequest}
                  disabled={printBusy}
                  aria-label={t("pane.print.tooltip")}
                  sx={{
                    color: "text.secondary",
                    opacity: 0.85,
                    "&:hover": { opacity: 1, color: "primary.main" },
                  }}
                >
                  {printBusy ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PrintRoundedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {selectedConversation?.id && !isMobile && !isPersonalThread && !hideOpenPopupButton && (
            <IconButton onClick={onOpenPopup} title={t("pane.openPopup")}>
              <OpenInNewIcon />
            </IconButton>
          )}
          {selectedConversation &&
            !detailsAccessDenied &&
            !showConversationDetails &&
            !isPersonalThread &&
            !hideDetailsToggle && (
            <IconButton onClick={onShowDetails}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {!popupCallMode && <Divider />}

      <ConversationQueueKeyboardHint open={showQueueKeyboardHint} />

      {messagesSyncError &&
        selectedConversation &&
        !detailsAccessDenied &&
        !isPersonalThread &&
        !(messagesDupOpen && messagesSyncError?.code === "errors.clients.duplicate") && (
          <Alert
            severity={
              messagesSyncError.code === "errors.clients.duplicate" ? "warning" : "error"
            }
            sx={{ borderRadius: 0 }}
            action={
              typeof reloadConversationMessages === "function" ? (
                <Button color="inherit" size="small" onClick={() => reloadConversationMessages()}>
                  Tentar novamente
                </Button>
              ) : null
            }
          >
            {messagesSyncError.message ||
              "Não foi possível carregar o histórico desta conversa."}
            {messagesSyncError.code === "errors.clients.duplicate" ? (
              <Typography variant="caption" component="div" sx={{ display: "block", mt: 0.75 }}>
                Abra o aviso de duplicidade para unificar os cadastros ou ajustar o telefone.
              </Typography>
            ) : null}
          </Alert>
        )}

      {showGupshupSipVoiceBar && (
        <Alert
          severity="info"
          icon={<PhoneIcon fontSize="inherit" />}
          sx={{ borderRadius: 0, py: 0.75, alignItems: "center" }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {t("pane.whatsappVoice.hint")}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                disabled={gupshupVoiceBusy}
                onClick={handleGupshupRequestPermission}
                sx={{ textTransform: "none" }}
              >
                {t("pane.whatsappVoice.requestPermission")}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={gupshupVoiceBusy}
                onClick={handleGupshupTwilioCall}
                sx={{ textTransform: "none" }}
              >
                {t("pane.whatsappVoice.startCall")}
              </Button>
            </Stack>
          </Stack>
          {gupshupVoiceNotice && (
            <Typography
              variant="caption"
              component="div"
              sx={{
                mt: 1,
                color:
                  gupshupVoiceNotice.severity === "error"
                    ? "error.main"
                    : gupshupVoiceNotice.severity === "warning"
                      ? "warning.main"
                      : "success.main",
              }}
            >
              {gupshupVoiceNotice.text}
            </Typography>
          )}
        </Alert>
      )}

      {isLoadingDetails && !initialLoading && (
        <LinearProgress sx={{ width: "100%", position: "absolute", top: 0 }} />
      )}

      {detailsAccessDenied ? (
        <ConversationAccessDenied
          denied={detailsDenied}
          conversationId={selectedConversationId}
        />
      ) : selectedConversation && (!isLoadingDetails || !initialLoading) ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {isWhatsAppQrChannelTipo(channelTipoForWindow) ? (
            <WhatsappQrCallBanner
              channelId={channelIdForWindow}
              version={channelRowForWindow?.whatsapp_qr_api_version}
            />
          ) : null}
          <MessageHistory
          messages={messages}
          selectedConversation={selectedConversation}
          isLoadingConversation={isLoadingConversation}
          onSendMessage={onSendMessage}
          onEndConversation={onEndConversation}
          onReopenRequest={() => handleStatusChange(1)}
          onOpenNewRequest={onNewConversation}
          hasMoreMessages={hasMoreMessages}
          isLoadingMoreMessages={isLoadingMoreMessages}
          onLoadMoreMessages={onLoadMoreMessages}
          onDeleteMessage={onDeleteMessage}
          onReactMessage={onReactMessage}
          onEditWhatsappMessage={onEditWhatsappMessage}
          onForwardWhatsappMessage={onForwardWhatsappMessage}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          onRefetchConversationDetails={onRefetchConversationDetails}
          onComposeChannelChange={handleComposeChannelChange}
        />
        </Box>
      ) : awaitingConversationDetails ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        !isMobile && <Placeholder />
      )}

      <TypeKnowledgeMaterialsDialog
        open={knowledgeMaterialsOpen}
        onClose={() => setKnowledgeMaterialsOpen(false)}
        conversationId={conversationIdForKnowledge}
      />

      <PromoteToRequestDialog
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        onSubmit={handlePromoteSubmit}
        isLoading={promoteLoading}
        currentUser={userData}
        messages={messages}
        peerPhone={peerPhone}
        peerDisplayName={selectedConversation?.name || selectedConversation?.nome || ""}
        peerAvatar={selectedConversation?.profile_picture || null}
        error={promoteError}
      />

      <DuplicateClientDialog
        open={messagesDupOpen}
        duplicates={messagesSyncError?.duplicates || []}
        attemptedPayload={mergeAttemptedPayload}
        secondaryClientId={mergeSecondaryClientId}
        secondaryLabel="cliente vinculado a esta conversa"
        onClose={() => setMessagesDupOpen(false)}
        onChangePhone={() => {
          setMessagesDupOpen(false);
          onShowDetails?.();
        }}
        onMerged={async () => {
          setMessagesDupOpen(false);
          if (typeof onRefetchConversationDetails === "function") {
            await onRefetchConversationDetails();
          }
          if (typeof reloadConversationMessages === "function") {
            await reloadConversationMessages();
          }
        }}
      />

      <DuplicateClientDialog
        open={clientDupDialog.open}
        duplicates={clientDupDialog.duplicates}
        attemptedPayload={clientDupDialog.attemptedPayload}
        secondaryClientId={clientDupDialog.secondaryClientId}
        secondaryLabel="cliente desta conversa"
        onClose={() => setClientDupDialog((s) => ({ ...(s || {}), open: false }))}
        onChangePhone={() => {
          setClientDupDialog((s) => ({ ...(s || {}), open: false }));
          onShowDetails?.();
        }}
        onMerged={async () => {
          setClientDupDialog((s) => ({ ...(s || {}), open: false }));
          if (typeof onRefetchConversationDetails === "function") {
            await onRefetchConversationDetails();
          }
        }}
      />

      <Dialog open={deletePersonalOpen} onClose={() => !deletePersonalBusy && setDeletePersonalOpen(false)}>
        <DialogTitle>{t("pane.deletePersonalConfirmTitle")}</DialogTitle>
        <DialogContent>{t("pane.deletePersonalConfirmBody")}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePersonalOpen(false)} disabled={deletePersonalBusy}>
            {t("pane.deletePersonalCancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deletePersonalBusy}
            onClick={async () => {
              if (typeof onDeletePersonalConversation !== "function") return;
              setDeletePersonalBusy(true);
              try {
                await onDeletePersonalConversation();
                setDeletePersonalOpen(false);
              } catch {
                /* snackbar no pai */
              } finally {
                setDeletePersonalBusy(false);
              }
            }}
          >
            {t("pane.deletePersonalConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <WhatsappGroupManageDialog
        open={groupManageOpen}
        onClose={() => setGroupManageOpen(false)}
        channelId={channelIdForWindow}
        groupJid={groupJidForManage}
        projectId={
          selectedConversation?.projectId ??
          selectedConversation?.id_projeto ??
          selectedConversation?.idProjeto ??
          null
        }
      />

      <AutomationActionBlockedDialog
        open={automationBlockDialog.open}
        message={automationBlockDialog.message}
        onClose={() => setAutomationBlockDialog({ open: false, message: "" })}
      />

      <Snackbar
        open={statusSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setStatusSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={statusSnackbar.severity}
          variant="filled"
          onClose={() => setStatusSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%", maxWidth: 560 }}
        >
          {statusSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageHistoryPane;
